/**
 * Replays the outbox: one tab at a time, one entity in order, several
 * entities at once.
 *
 * The HTTP sender is injected by the api module at load time, so this file
 * never imports it. The cross-tab lock is `navigator.locks`; where the
 * browser has no Web Locks, writes still queue but never replay here, and the
 * state says so once there is something waiting. A flush requested while the
 * lock is held, by this tab or another, is honoured with one more drain.
 *
 * A policy's `onConflict` runs outside every entity lock, but on replay the
 * flusher still holds the flush lock, so a handler may call `flush()` (it only
 * requests a re-drain) but must never await `wipeOfflineStore()`.
 */

import { broadcastInvalidated } from './cache';
import { localStoreOf, revisionKey } from './echo';
import { withEntityLock } from './entityLock';
import { isNetworkError } from './errors';
import { markRow, refreshCounts, registerOutboxFlushScheduler, removeRow } from './outbox';
import { patchOutboxState } from './outboxState';
import { captureScope, listOutbox, scopeIsCurrent, withStores } from './store';
import { OFFLINE_CHANNEL, TAB_ID } from './wipe';
import { registerFlushScheduler } from './write';
import { writePolicyFor } from './writePolicies';

import type { OutboxRow, WriteMethod, WriteScope } from './store';
import type { ConflictOutcome, WritePolicy, WriteRequest } from './writePolicies';
import type { ApiError } from '@shared/services/requestTypes';

/** How a queued row reaches the network; the api module owns the real one. */
export type Sender = (method: WriteMethod, url: string, body: unknown) => Promise<unknown>;

const LOCK_NAME = 'pa-offline-flush';
const CONCURRENT_ENTITIES = 4;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const MIN_WAKE_UP_MS = 1000;
/** Name of the window event auth.ts dispatches on sign-in and sign-out (AUTH_STATE_CHANGE_EVENT). */
const AUTH_EVENT = 'authStateChange';
/** Name of the window event auth.ts dispatches on a same-user token refresh (TOKEN_ROTATED_EVENT). */
const TOKEN_ROTATED_EVENT = 'authTokenRotated';

let sender: Sender | null = null;
let draining = false;
let requestedWhileDraining = false;
let wakeUp: ReturnType<typeof setTimeout> | null = null;

/** The api module hands over its raw sender at load; tests hand over a fake. */
export function registerSender(fn: Sender): void {
  sender = fn;
}

/** The raw network sender, for conflict handlers that must bypass the write seam. Throws before the api module registers it. */
export function sendRaw(method: WriteMethod, url: string, body: unknown): Promise<unknown> {
  if (!sender) throw new Error('offline: no sender registered');
  return sender(method, url, body);
}

/** Whether this browser can hold the cross-tab lock the flusher needs. */
export function locksAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';
}

/** Delay for the n-th consecutive 5xx: 2^n seconds, capped. */
const backoffMs = (attempts: number): number => Math.min(2 ** attempts * 1000, MAX_BACKOFF_MS);

/** The request a row replays. */
const requestOf = (row: OutboxRow): WriteRequest => ({
  method: row.method,
  url: row.path,
  body: row.body,
});

type Step =
  | { kind: 'done' }
  | { kind: 'accepted' }
  | { kind: 'skipped' }
  | { kind: 'stop-entity' }
  | { kind: 'stop-all' }
  | { kind: 'conflict'; err: ApiError };

/**
 * One drain's shared "give up" flag.
 *
 * Entities drain in parallel, so a row can already be past its claim when a
 * sibling's send discovers the network is gone. The flag is raised the moment
 * that failure is seen — before the store is touched — and every worker reads
 * it again immediately before its own send, which is what keeps a single
 * outage from costing one doomed request per entity.
 */
interface StopSignal {
  all: boolean;
}

/** Mark the row in flight only if it is still the row we read; false when it was coalesced or discarded. */
async function claim(scope: WriteScope, row: OutboxRow): Promise<boolean> {
  const claimed = await withStores(scope, 'readwrite', async tx => {
    const outbox = tx.objectStore('outbox');
    const current = await outbox.get(row.id);
    if (!current || current.state !== 'queued' || current.seq !== row.seq) return false;
    // `attempts` counts sends, so the outbox can tell a create that never left
    // the device from one whose reply was lost.
    await outbox.put({ ...current, state: 'inflight', attempts: current.attempts + 1 });
    return true;
  });
  return claimed === true;
}

/** The expected revision for a row: threaded from the last reply, else the cached copy's, else none. */
async function expectedRevisionFor(
  scope: WriteScope,
  row: OutboxRow,
  policy: WritePolicy,
  req: WriteRequest
): Promise<string | null> {
  const revision = policy.revision;
  if (!revision || row.dependsOnCreate) return null;
  const found = await withStores(scope, 'readonly', async tx => {
    const threaded = (await tx.objectStore('meta').get(revisionKey(row.entityKey)))?.value as
      | string
      | undefined;
    if (threaded) return threaded;
    return revision.fromCache ? await revision.fromCache(localStoreOf(tx), req) : null;
  });
  return found ?? null;
}

/**
 * Put a claimed row back in the queue because the drain is giving up, not
 * because the row is at fault; its backoff is already due, so it is dropped.
 */
async function requeue(scope: WriteScope, row: OutboxRow): Promise<void> {
  await markRow(scope, row.id, { state: 'queued', nextAttemptAt: undefined });
}

/**
 * Undo a claim the drain never used.
 *
 * The row goes back with the send count it had *before* the claim: a claim
 * that never reached the network is not a send, and `attempts` is what tells
 * a create that never left the device from one whose reply was lost — a
 * delete of the first cancels the entity, a delete of the second must not.
 */
async function unclaim(scope: WriteScope, row: OutboxRow): Promise<void> {
  await markRow(scope, row.id, {
    state: 'queued',
    nextAttemptAt: undefined,
    attempts: row.attempts,
  });
}

/**
 * A failed send the policy settles itself: a status it accepts (the row is
 * done, no reply), or a 409 its conflict handler will take outside the lock.
 * Null when neither applies and the failure table decides.
 */
function settledByPolicy(
  policy: WritePolicy,
  status: number | undefined,
  err: unknown
): Step | null {
  if (status === undefined) return null;
  if (policy.acceptStatus?.(status)) return { kind: 'accepted' };
  if (status === 409 && policy.onConflict) return { kind: 'conflict', err: err as ApiError };
  return null;
}

/** The failure table from the spec, for everything the policy does not settle itself. */
async function handleFailure(
  scope: WriteScope,
  row: OutboxRow,
  policy: WritePolicy,
  err: unknown,
  stop: StopSignal
): Promise<Step> {
  const status = (err as ApiError).status;
  if (isNetworkError(err)) {
    stop.all = true; // raised before any await, so a sibling's send sees it
    await requeue(scope, row);
    return { kind: 'stop-all' };
  }
  if (status === 401) {
    stop.all = true;
    await requeue(scope, row);
    patchOutboxState({ needsSignIn: true });
    return { kind: 'stop-all' };
  }
  const settled = settledByPolicy(policy, status, err);
  if (settled) return settled;
  if (status !== undefined && status >= 500) {
    const attempts = row.attempts + 1; // the claim already counted this send
    await markRow(scope, row.id, {
      state: 'queued',
      nextAttemptAt: Date.now() + backoffMs(attempts),
    });
    return { kind: 'stop-entity' };
  }
  const message = err instanceof Error ? err.message : 'Request failed';
  await markRow(scope, row.id, { state: 'failed', lastStatus: status, lastError: message });
  return { kind: 'stop-entity' };
}

/**
 * Record a write the server took: thread the reply's revision, drop the row,
 * re-echo what is left, and tell every tab which cached keys changed meaning.
 * A status the policy accepts arrives with no reply (`null`): the row is
 * dropped and nothing is threaded.
 */
async function acknowledge(
  scope: WriteScope,
  row: OutboxRow,
  policy: WritePolicy,
  req: WriteRequest,
  reply: { data: unknown } | null
): Promise<Step> {
  if (reply) {
    const threadForward = policy.revision && policy.revision.threadForward !== false;
    const revision = threadForward ? policy.revision!.fromResponse(reply.data) : null;
    await removeRow(scope, row.id, revision, reply.data);
  } else {
    await removeRow(scope, row.id);
  }
  if (scopeIsCurrent(scope)) patchOutboxState({ lastSyncAt: Date.now() });
  // The cached copy now carries what the server answered (a revision, an id);
  // an open editor, in whichever tab, needs to hear that its queued save landed.
  broadcastInvalidated(policy.touches?.(req) ?? [], 'acknowledged');
  return { kind: 'done' };
}

/** Claim, send, and acknowledge one row under the entity lock; a 409 is reported, not handled, here. */
async function replayLocked(
  scope: WriteScope,
  row: OutboxRow,
  policy: WritePolicy,
  send: Sender,
  stop: StopSignal
): Promise<Step> {
  const req = requestOf(row);
  if (!(await claim(scope, row))) return { kind: 'skipped' };
  try {
    const expected = await expectedRevisionFor(scope, row, policy, req);
    const body = policy.revision ? policy.revision.withExpected(row.body, expected) : row.body;
    if (stop.all || !scopeIsCurrent(scope)) {
      await unclaim(scope, row);
      return { kind: 'stop-all' };
    }
    let data: unknown;
    try {
      data = await send(row.method, row.path, body);
    } catch (err) {
      const step = await handleFailure(scope, row, policy, err, stop);
      if (step.kind !== 'accepted') return step;
      return await acknowledge(scope, row, policy, req, null);
    }
    return await acknowledge(scope, row, policy, req, { data });
  } catch (err) {
    // A revision adapter, the store, or anything else threw: try not to leave
    // the row in flight. If even that fails the store is gone, and the next
    // flusher to take the lock resets in-flight rows anyway.
    const message = err instanceof Error ? err.message : 'Replay failed';
    await markRow(scope, row.id, { state: 'failed', lastError: message }).catch(() => undefined);
    return { kind: 'stop-entity' };
  }
}

/** Replay one row: the locked part, then a conflict handler outside the lock if one is due. */
async function replay(
  scope: WriteScope,
  row: OutboxRow,
  send: Sender,
  stop: StopSignal
): Promise<Step> {
  const req = requestOf(row);
  const policy = writePolicyFor(req);
  if (!policy) {
    await markRow(scope, row.id, {
      state: 'failed',
      lastError: 'No write policy for this request',
    });
    return { kind: 'stop-entity' };
  }
  const step = await withEntityLock(row.entityKey, () =>
    replayLocked(scope, row, policy, send, stop)
  );
  if (step.kind !== 'conflict') return step;
  let result: ConflictOutcome;
  try {
    result = await policy.onConflict!(req, step.err, scope);
  } catch {
    result = 'failed';
  }
  return withEntityLock(row.entityKey, async (): Promise<Step> => {
    if (result === 'failed') {
      await markRow(scope, row.id, { state: 'failed', lastStatus: 409, lastError: 'Conflict' });
      return { kind: 'stop-entity' };
    }
    await removeRow(scope, row.id);
    // The handler settled the row with the server one way or another; an
    // editor waiting on this save would otherwise wait forever. A handler
    // that announced the outcome itself ('resolved-announced') has already
    // said what happened, and "acknowledged" on top would contradict it.
    if (result === 'resolved') broadcastInvalidated(policy.touches?.(req) ?? [], 'acknowledged');
    return { kind: 'done' };
  });
}

/** Rows grouped by entity, each group in seq order, groups by their first row. */
function groupByEntity(rows: OutboxRow[]): OutboxRow[][] {
  const groups = new Map<string, OutboxRow[]>();
  for (const row of rows) groups.set(row.entityKey, [...(groups.get(row.entityKey) ?? []), row]);
  return [...groups.values()];
}

/** Everything under the lock: reset dead in-flight rows, then drain entities four at a time. */
async function drain(scope: WriteScope, send: Sender): Promise<void> {
  for (const row of await listOutbox(scope.account)) {
    if (row.state === 'inflight') await markRow(scope, row.id, { state: 'queued' });
  }
  const groups = groupByEntity(await listOutbox(scope.account));
  const stop: StopSignal = { all: false };
  let next = 0;
  const worker = async (): Promise<void> => {
    while (!stop.all && next < groups.length) {
      const group = groups[next]!;
      next += 1;
      for (const row of group) {
        if (stop.all) return;
        if (row.state === 'failed') break;
        if (row.nextAttemptAt && row.nextAttemptAt > Date.now()) break;
        const step = await replay(scope, row, send, stop);
        if (step.kind === 'stop-all') {
          stop.all = true;
          return;
        }
        if (step.kind === 'stop-entity') break;
      }
    }
  };
  await Promise.allSettled(
    Array.from({ length: Math.min(CONCURRENT_ENTITIES, groups.length) }, worker)
  );
}

/**
 * Arm a timer for the earliest backed-off row that is not stuck behind
 * another.
 *
 * Only the head of each entity is considered: rows behind it cannot run until
 * it does, so a backoff further down the queue is not a reason to wake up —
 * and a head that failed blocks its entity until the user retries or discards
 * it, so that entity contributes no wake-up at all.
 */
async function scheduleWakeUp(scope: WriteScope): Promise<void> {
  if (wakeUp) clearTimeout(wakeUp);
  wakeUp = null;
  let soonest: number | null = null;
  for (const group of groupByEntity(await listOutbox(scope.account))) {
    const head = group[0];
    if (!head || head.state !== 'queued' || !head.nextAttemptAt) continue;
    soonest = soonest === null ? head.nextAttemptAt : Math.min(soonest, head.nextAttemptAt);
  }
  if (soonest === null) return;
  wakeUp = setTimeout(() => void flush('backoff'), Math.max(MIN_WAKE_UP_MS, soonest - Date.now()));
}

/** Ask whoever holds the flush lock (maybe another tab) to drain once more. */
function requestFlushEverywhere(): void {
  requestedWhileDraining = true;
  if (typeof BroadcastChannel === 'undefined') return;
  const bc = new BroadcastChannel(OFFLINE_CHANNEL);
  bc.postMessage({ type: 'flush-requested', from: TAB_ID });
  bc.close();
}

/**
 * Replay the outbox if this tab can take the lock. When another tab holds it,
 * ask that tab to drain again instead. A no-op when the browser has no Web
 * Locks.
 */
export async function flush(_trigger: string): Promise<void> {
  if (!sender) return;
  const scope = captureScope();
  if (!scope) return;
  if (!locksAvailable()) {
    patchOutboxState({ locksUnavailable: (await listOutbox(scope.account)).length > 0 });
    return;
  }
  if (draining) {
    requestedWhileDraining = true;
    return;
  }
  const send = sender;
  await navigator.locks.request(LOCK_NAME, { ifAvailable: true }, async lock => {
    if (!lock) {
      requestFlushEverywhere();
      return;
    }
    draining = true;
    patchOutboxState({ syncing: true, needsSignIn: false });
    try {
      do {
        requestedWhileDraining = false;
        await drain(scope, send);
      } while (requestedWhileDraining);
    } finally {
      draining = false;
      patchOutboxState({ syncing: false });
      await refreshCounts();
      await scheduleWakeUp(scope);
    }
  });
}

/**
 * Wire the flush triggers: boot, `online`, tab becoming visible (which also
 * refreshes the counts another tab may have changed), a successful sign-in or
 * token refresh, and another tab's flush request. Returns a disposer.
 */
export function installFlushTriggers(): () => void {
  const onOnline = () => void flush('online');
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    void refreshCounts().then(() => flush('visible'));
  };
  const onAuth = (event: Event) => {
    if ((event as CustomEvent<{ isAuthenticated: boolean }>).detail?.isAuthenticated) {
      void flush('auth');
    }
  };
  const onRotated = () => void flush('token-rotated');
  const bc = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(OFFLINE_CHANNEL);
  if (bc) {
    bc.onmessage = (event: MessageEvent<{ type: string; from?: string }>) => {
      if (event.data?.type !== 'flush-requested' || event.data.from === TAB_ID) return;
      if (draining) requestedWhileDraining = true;
      else void flush('other-tab');
    };
  }
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener(AUTH_EVENT, onAuth);
  window.addEventListener(TOKEN_ROTATED_EVENT, onRotated);
  void flush('boot');
  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener(AUTH_EVENT, onAuth);
    window.removeEventListener(TOKEN_ROTATED_EVENT, onRotated);
    bc?.close();
    if (wakeUp) clearTimeout(wakeUp);
  };
}

/** Tests start with no sender, nothing draining, and no timer. */
export function resetFlusherForTests(): void {
  sender = null;
  draining = false;
  requestedWhileDraining = false;
  if (wakeUp) clearTimeout(wakeUp);
  wakeUp = null;
}

registerFlushScheduler(trigger => void flush(trigger));
registerOutboxFlushScheduler(trigger => void flush(trigger));
